/**
 * Sprint 26.0 — edit-in-place task title.
 *
 * A `contenteditable` <h2>. Saves on blur or Enter (no Shift); optimistic local
 * update, reconciled from the server response by the parent. In create mode it
 * focuses on mount with a placeholder.
 */

import { useEffect, useRef, useState, type CSSProperties } from "react";

export interface EditableTitleProps {
  value: string;
  placeholder?: string;
  autoFocus?: boolean;
  onSave: (text: string) => void;
  /** Live-report the current text (create mode uses it to enable Create). */
  onChange?: (text: string) => void;
}

export function EditableTitle({ value, placeholder, autoFocus, onSave, onChange }: EditableTitleProps) {
  const ref = useRef<HTMLHeadingElement>(null);
  const [focused, setFocused] = useState(false);

  // Keep the DOM text in step with the prop when not actively editing.
  useEffect(() => {
    if (ref.current && !focused && ref.current.textContent !== value) {
      ref.current.textContent = value;
    }
  }, [value, focused]);

  useEffect(() => {
    if (autoFocus && ref.current) {
      ref.current.focus();
    }
  }, [autoFocus]);

  const empty = value.trim().length === 0;

  return (
    <div style={{ position: "relative" }}>
      <h2
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        data-testid="editable-title"
        data-placeholder={placeholder}
        style={{ ...title, ...(focused ? focusedStyle : {}), ...(empty && !focused ? emptyStyle : {}) }}
        onFocus={() => setFocused(true)}
        onBlur={(e) => {
          setFocused(false);
          const text = e.currentTarget.textContent?.trim() ?? "";
          if (text !== value.trim()) onSave(text);
        }}
        onInput={(e) => onChange?.(e.currentTarget.textContent ?? "")}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            (e.currentTarget as HTMLElement).blur();
          }
        }}
      >
        {value}
      </h2>
      {empty && !focused && placeholder && <span style={placeholderStyle}>{placeholder}</span>}
      <i className="ti ti-pencil" style={pencil} aria-hidden="true" />
    </div>
  );
}

const title: CSSProperties = {
  fontSize: 18,
  fontWeight: 500,
  color: "var(--color-text-primary)",
  margin: 0,
  outline: "none",
  paddingRight: 22,
  lineHeight: 1.3,
};

const focusedStyle: CSSProperties = {
  background: "color-mix(in srgb, var(--color-background-secondary) 50%, transparent)",
  borderRadius: "var(--border-radius-sm)",
};

const emptyStyle: CSSProperties = {
  minHeight: "1.3em",
};

const placeholderStyle: CSSProperties = {
  position: "absolute",
  top: 0,
  left: 0,
  fontSize: 18,
  fontWeight: 500,
  color: "var(--color-text-tertiary)",
  pointerEvents: "none",
};

const pencil: CSSProperties = {
  position: "absolute",
  top: 3,
  right: 0,
  fontSize: 13,
  color: "var(--color-text-tertiary)",
};
